import { useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useBoolean } from '@sravni/react-utils';
import { isEqual } from 'lodash';
import { profile } from '../../../../helpers/client';
import { useFormData, useFormProfileMetaData, useFormStore } from '../../../../store/selectors/formStore';
import { omit, pick, removeUndefined } from '../../../../helpers';
import {
    CLEARABLE_FORM_FIELDS,
    FORM_FIELDS,
    PROFILE_PEOPLE_FIELDS_MAP,
    REQUIRED_FIELDS_FOR_PROFILE_PEOPLE_UPDATE,
} from '../../../../constants';
import { useIsPage } from '../../../../store/selectors/selection';
import { extendFormData, extendFormStore } from '../../../../store/reducers/formStore';
import { getUserIdFromWlStorageItem, loadWlAuthorizedForm, saveWlAuthorizedForm } from '../wl.local';
import { clearSessionStorageFormData, isNewData } from '../helpers';
import { PEOPLE_NAMES } from '../../../../services/profile.graphql/constants';
import {
    addAddressDadataFields,
    addRequiredPledgeFields,
    checkProfileDataHasAllRequiredFields,
    checkUpdateDataHasAnyRequiredFields,
    removeAddressFieldsWithoutDadata,
    removePledgeFieldsWithoutRequired,
} from './helpers';

export const MANUAL_PROFILE_UPDATE_EVENT = 'manualProfileUpdate';

export const getProfileUpdateEvent = (detail?: Record<string, unknown>) =>
    new CustomEvent(MANUAL_PROFILE_UPDATE_EVENT, {
        bubbles: true,
        cancelable: false,
        detail,
    });

// TODO: [Profile Graphql] если в SessionStorage были сохранены данные и пользователь попал на выдачу после перезагрузки (autoRun), то сделать update в профиль (при успешном попадании на выдачу)
export const useProfile = (
    enabled = false,
    currentStep = 1,
    excludedFields: Array<keyof FE.FormData> = [],
    onUpdateProfileData?: (updatedFields: Array<keyof FE.FormData>) => void,
) => {
    const { userId: userIdFromStorage } = useFormStore();
    const formData = useFormData();
    const profileMetaData = useFormProfileMetaData();
    const { isWlPage, isWaybackPage, isCardsFormPage } = useIsPage();
    const [updateProfileTrigger, { toggle: toggleUpdateProfileTrigger, off: offUpdateProfileTrigger }] = useBoolean();
    const prevFormDataRef = useRef<FE.FormData>(removeUndefined(omit(formData, excludedFields), [''], true));
    const prevStepRef = useRef(currentStep);
    const isWlStorageLoaded = useRef(false);
    const formProfileData = useMemo(() => pick(formData, Object.keys(PROFILE_PEOPLE_FIELDS_MAP)), [formData]);
    const dispatch = useDispatch();

    const userIdToProfile = isWlPage ? undefined : userIdFromStorage;

    const update = (isTriggeredByEvent = false) => {
        if (!enabled || !userIdFromStorage || !isNewData(prevFormDataRef.current, formProfileData)) {
            return;
        }

        const newData = omit(formProfileData, excludedFields);

        const dataToSave = checkUpdateDataHasAnyRequiredFields(newData)
            ? removeUndefined(formProfileData, [''], true, CLEARABLE_FORM_FIELDS)
            : newData;

        if (Object.keys(dataToSave).length === 0) {
            return;
        }

        addAddressDadataFields(dataToSave, formProfileData);
        addRequiredPledgeFields(dataToSave, formProfileData);

        removeAddressFieldsWithoutDadata(dataToSave);
        removePledgeFieldsWithoutRequired(dataToSave);

        // для WL страниц при любом изменении поля дополнительно сохранять анкету в WL Session Storage
        if (isWlPage && formProfileData) {
            const wlData = removeUndefined(formData, [''], true, CLEARABLE_FORM_FIELDS);
            saveWlAuthorizedForm(wlData, userIdFromStorage);
        }

        if (
            !isWaybackPage &&
            Object.keys(dataToSave).length > 0 &&
            // в анкете заполнены обязательные поля firstName, lastName, birthDay
            checkProfileDataHasAllRequiredFields(formProfileData, REQUIRED_FIELDS_FOR_PROFILE_PEOPLE_UPDATE) &&
            // при вызове события обновления профиля ИЛИ при переходе на следующий шаг ИЛИ при изменении одного из обязательных полей
            (isTriggeredByEvent || isCardsFormPage || currentStep > prevStepRef.current)
        ) {
            profile
                .update(dataToSave, userIdToProfile)
                .then((isUpdated) => {
                    // При успешном сохранении данных очищать Session Storage
                    if (isUpdated) {
                        clearSessionStorageFormData();
                        const changedKeys = Object.keys(dataToSave).filter(
                            (key) => !isEqual(dataToSave[key], prevFormDataRef.current[key]),
                        );
                        onUpdateProfileData?.(changedKeys);
                    }
                })
                .finally(() => (prevFormDataRef.current = { ...formProfileData }));
        }
    };

    const updateDataFromEsia = () => {
        if (!enabled || !userIdFromStorage) {
            return;
        }

        const dataToSave = removeUndefined(formProfileData, [''], true, CLEARABLE_FORM_FIELDS);

        if (Object.keys(dataToSave).length === 0) {
            return;
        }

        removeAddressFieldsWithoutDadata(dataToSave);
        removePledgeFieldsWithoutRequired(dataToSave);

        if (
            !isWaybackPage &&
            checkProfileDataHasAllRequiredFields(formProfileData, REQUIRED_FIELDS_FOR_PROFILE_PEOPLE_UPDATE)
        ) {
            profile.update(removeUndefined(formProfileData, [''], true), userIdToProfile);
        }
    };

    const loadFromWlSessionStorage = () => {
        if (!enabled || !isWlPage || isWlStorageLoaded.current) {
            return;
        }
        const wlUserId = getUserIdFromWlStorageItem();
        if (!wlUserId || wlUserId !== userIdFromStorage) {
            return;
        }
        const dataParsed = loadWlAuthorizedForm(wlUserId);
        if (!dataParsed || !dataParsed[FORM_FIELDS.PHONE]) {
            return;
        }

        const data = removeUndefined(dataParsed, [''], true, CLEARABLE_FORM_FIELDS);
        prevFormDataRef.current = data;
        isWlStorageLoaded.current = true;
        dispatch(extendFormData(data));
        dispatch(extendFormStore({ autoRun: true }));
    };

    useEffect(() => {
        // TODO: [Profile Graphql] разобраться, как иначе сохранять данные в профиль на выдаче (при изменении фильтров / полей залога авто и недвижимости)
        document.addEventListener(MANUAL_PROFILE_UPDATE_EVENT, toggleUpdateProfileTrigger, false);
        if (profileMetaData && profileMetaData.selectedPeople === PEOPLE_NAMES.ESIA) {
            updateDataFromEsia();
        }
        return () => {
            document.removeEventListener(MANUAL_PROFILE_UPDATE_EVENT, toggleUpdateProfileTrigger, false);
        };
    }, []);

    // загрузка данных из SessionStorage для WL страниц
    useEffect(() => {
        loadFromWlSessionStorage();
    }, [enabled, dispatch, isWlPage]);

    // сохранение данных в Profile Service
    useEffect(() => {
        update();
        // переписываем профиль только на изменение шага анкеты
    }, [currentStep, userIdFromStorage]);

    useEffect(() => {
        if (updateProfileTrigger) {
            update(true);
            offUpdateProfileTrigger();
        }
    }, [updateProfileTrigger]);

    useEffect(() => {
        prevStepRef.current = currentStep;
    }, [currentStep]);
};
